import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./login.scss";
import { getApiInstance } from "../../utils/api";
import { mensajeErrorAuth, normalizarCodigo } from "../../utils/authUtils";
import Input from "../../components/inputs/input/input";
import Button from "../../components/button/button";
import LoginLayout from "./loginLayout";

// Pasos del acceso: email + contraseña -> código enviado por email (si el API tiene SMTP)
// y, aparte, "¿Has olvidado tu contraseña?".
const PASO = {
    CREDENCIALES: "credenciales",
    CODIGO: "codigo",
    OLVIDO: "olvido",
};

const Login = ({ onLogin }) => {
    const [paso, setPaso] = useState(PASO.CREDENCIALES);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [codigo, setCodigo] = useState("");
    const [error, setError] = useState("");
    const [aviso, setAviso] = useState("");
    const [enviando, setEnviando] = useState(false);
    const navigate = useNavigate();

    const entrar = (token) => {
        localStorage.setItem("token", token);
        onLogin(token);
        navigate("/mobiliario-central/todo");
    };

    const irA = (nuevoPaso) => {
        setError("");
        setAviso("");
        setCodigo("");
        setPaso(nuevoPaso);
    };

    const pedirCodigo = async () => {
        const api = getApiInstance();
        const response = await api.post("/api/Auth/login", {
            email: username.trim(),
            password: password,
        });

        if (response.data?.requiresTwoFactor) {
            setPaso(PASO.CODIGO);
            setAviso(
                response.data.message ||
                    "Te hemos enviado un código de verificación a tu email."
            );
            return;
        }

        entrar(response.data.token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (enviando) return;
        setError("");
        setAviso("");
        setEnviando(true);
        try {
            await pedirCodigo();
        } catch (error) {
            setError(
                mensajeErrorAuth(
                    error,
                    "Error al iniciar sesión. Por favor, verifica tus credenciales."
                )
            );
        } finally {
            setEnviando(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (enviando) return;
        if (codigo.length !== 6) {
            setError("Introduce los 6 dígitos del código.");
            return;
        }
        setError("");
        setEnviando(true);
        try {
            const api = getApiInstance();
            const response = await api.post("/api/Auth/verify", {
                email: username.trim(),
                code: codigo,
            });
            entrar(response.data.token);
        } catch (error) {
            setCodigo("");
            setError(
                mensajeErrorAuth(
                    error,
                    "Código de verificación incorrecto o caducado."
                )
            );
        } finally {
            setEnviando(false);
        }
    };

    const handleReenviar = async () => {
        if (enviando) return;
        setError("");
        setCodigo("");
        setEnviando(true);
        try {
            await pedirCodigo();
            setAviso("Te hemos enviado un código nuevo. El anterior ya no sirve.");
        } catch (error) {
            setError(mensajeErrorAuth(error, "No se ha podido reenviar el código."));
        } finally {
            setEnviando(false);
        }
    };

    const handleOlvido = async (e) => {
        e.preventDefault();
        if (enviando) return;
        if (!username.trim()) {
            setError("Introduce tu email.");
            return;
        }
        setError("");
        setEnviando(true);
        try {
            const api = getApiInstance();
            const response = await api.post("/api/Auth/forgot-password", {
                email: username.trim(),
            });
            setAviso(
                response.data?.message ||
                    "Si el correo está registrado, recibirás un enlace para definir una nueva contraseña."
            );
        } catch (error) {
            setError(
                mensajeErrorAuth(error, "No se ha podido enviar la solicitud.")
            );
        } finally {
            setEnviando(false);
        }
    };

    const estadoBoton = enviando ? "loading" : "default";

    return (
        <LoginLayout>
                {paso === PASO.CREDENCIALES && (
                    <>
                        <h2 className="login__right__title">Iniciar Sesión</h2>
                        <form className="login__right__form" onSubmit={handleSubmit}>
                            <Input
                                type="text"
                                label="Email"
                                name="email"
                                placeholder="Introduce tu email"
                                value={username}
                                autoComplete="username"
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <Input
                                type="password"
                                label="Contraseña"
                                name="contraseña"
                                placeholder="Introduce tu contraseña"
                                value={password}
                                autoComplete="current-password"
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <div className="login__right__form__button">
                                <Button
                                    label="Iniciar sesión"
                                    variant="filled"
                                    type="submit"
                                    fullWidth={true}
                                    state={estadoBoton}
                                />
                            </div>
                            {error && (
                                <p className="login__right__form__error">{error}</p>
                            )}
                            <button
                                type="button"
                                className="login__right__form__link"
                                onClick={() => irA(PASO.OLVIDO)}
                            >
                                ¿Has olvidado tu contraseña?
                            </button>
                            <p className="login__right__form__info">
                                Si tienes problemas para iniciar sesión o si todavía no
                                tienes una cuenta, ponte en contacto con un
                                administrador.
                            </p>
                        </form>
                    </>
                )}

                {paso === PASO.CODIGO && (
                    <>
                        <h2 className="login__right__title">Verifica tu acceso</h2>
                        <form className="login__right__form" onSubmit={handleVerify}>
                            {aviso && (
                                <p className="login__right__form__notice">
                                    {aviso} Revisa <strong>{username.trim()}</strong>{" "}
                                    (también la carpeta de correo no deseado).
                                </p>
                            )}
                            <Input
                                type="text"
                                label="Código de verificación"
                                name="codigo"
                                placeholder="000000"
                                value={codigo}
                                autoComplete="one-time-code"
                                inputMode="numeric"
                                autoFocus
                                onChange={(e) => setCodigo(normalizarCodigo(e.target.value))}
                            />
                            <div className="login__right__form__button">
                                <Button
                                    label="Verificar y entrar"
                                    variant="filled"
                                    type="submit"
                                    fullWidth={true}
                                    state={estadoBoton}
                                />
                            </div>
                            {error && (
                                <p className="login__right__form__error">{error}</p>
                            )}
                            <div className="login__right__form__links">
                                <button
                                    type="button"
                                    className="login__right__form__link"
                                    onClick={handleReenviar}
                                    disabled={enviando}
                                >
                                    Reenviar código
                                </button>
                                <button
                                    type="button"
                                    className="login__right__form__link"
                                    onClick={() => irA(PASO.CREDENCIALES)}
                                >
                                    Volver
                                </button>
                            </div>
                        </form>
                    </>
                )}

                {paso === PASO.OLVIDO && (
                    <>
                        <h2 className="login__right__title">Restablecer contraseña</h2>
                        <form className="login__right__form" onSubmit={handleOlvido}>
                            <p className="login__right__form__notice">
                                Escribe tu email y te enviaremos un enlace para definir
                                una contraseña nueva.
                            </p>
                            <Input
                                type="text"
                                label="Email"
                                name="email"
                                placeholder="Introduce tu email"
                                value={username}
                                autoComplete="username"
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <div className="login__right__form__button">
                                <Button
                                    label="Enviar enlace"
                                    variant="filled"
                                    type="submit"
                                    fullWidth={true}
                                    state={estadoBoton}
                                />
                            </div>
                            {aviso && (
                                <p className="login__right__form__notice">{aviso}</p>
                            )}
                            {error && (
                                <p className="login__right__form__error">{error}</p>
                            )}
                            <button
                                type="button"
                                className="login__right__form__link"
                                onClick={() => irA(PASO.CREDENCIALES)}
                            >
                                Volver a iniciar sesión
                            </button>
                        </form>
                    </>
                )}
        </LoginLayout>
    );
};

export default Login;
